import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { ColumnDef, Row } from '@tanstack/react-table';
import { PaginationMetadata } from 'api/common-schemas';
import {
  OrderItemLocationType,
  OrdersByLocationType,
} from 'api/orders/schemas';
import clsx from 'clsx';
import { ServerPaginatedTable } from 'components/Table/ServerPaginatedTable';
import SubRows from 'components/Table/SubRows';
import { UsePaginatedTableReturn } from 'hooks/usePaginatedTable';
import { Add, Remove } from 'react-ionicons';
import { EmptyOrdersIndex } from 'screens/AccessExpressV3/Orders/EmptyResults';
import { OrderStatus } from '../utils';

import ProgressIndicator from './ProgressIndicator';
import ServiceTypeTag, {
  mapServiceTypeToTag,
  ServiceType,
} from 'screens/AccessExpressV3/BaseComponents/SeviceType';

interface LocationTableProps<T, M = PaginationMetadata> {
  sortingState: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  paginatedTableProps: Omit<UsePaginatedTableReturn<T, M>, 'data' | 'metadata'>;
  tablePageData: OrdersByLocationType[];
}

const LocationTable = <ResultData, MetadataType = PaginationMetadata>({
  sortingState,
  onSortingChange,
  paginatedTableProps,
  tablePageData,
}: LocationTableProps<ResultData, MetadataType>) => {
  const getOrdersIndexTableDef = (): ColumnDef<OrdersByLocationType>[] => {
    const columns: ColumnDef<OrdersByLocationType>[] = [
      {
        id: 'expander',
        header: () => null,
        cell: ({ row }) => {
          if (!row.getCanExpand()) return null;
          return (
            <div
              className="rounded border border-content-base-default p-0.5 text-content-base-default"
              {...{
                onClick: row.getToggleExpandedHandler(),
                style: { cursor: 'pointer' },
              }}
            >
              {row.getIsExpanded() ? (
                <Remove color="currentColor" width="18px" height="18px" />
              ) : (
                <Add color="currentColor" width="18px" height="18px" />
              )}
            </div>
          );
        },
      },
      {
        header: 'Address',
        accessorKey: 'address',
        cell: ({ row }) => {
          return (
            <div>
              <p>{row.original.address}</p>
              <p className="text-xs text-content-base-subdued">
                {row.original.city}
                {row.original.state || row.original.zip_code ? ',' : null}{' '}
                {row.original.state} {row.original.zip_code}
              </p>
            </div>
          );
        },
      },
      {
        header: 'Overall status',
        accessorKey: 'percentage',
        cell: ({ row }) => (
          <ProgressIndicator
            overallStatus={row.original.overall_status}
            percentage={row.original.percentage}
          />
        ),
      },
      {
        header: 'Site name',
        accessorKey: 'site_name',
        cell: ({ row }) => <p>{row.original.site_name}</p>,
      },
      {
        header: '# of services',
        accessorKey: 'num_services',
      },
    ];
    return columns;
  };

  const getSubRowsOrdersIndexTableDef =
    (): ColumnDef<OrderItemLocationType>[] => {
      const columns: ColumnDef<OrderItemLocationType>[] = [
        {
          id: 'expender',
        },
        {
          header: () => null,
          id: 'product_name',
          accessorKey: 'product_name',

          cell: ({ row }) => (
            <ServiceTypeTag
              type={mapServiceTypeToTag(
                row.original.product_name as ServiceType,
              )}
              className="max-w-full"
            />
          ),
        },
        {
          id: 'g360_status',
          accessorKey: 'g360_status',
          cell: ({ row }) => {
            return (
              <p className="flex items-center gap-2">
                <span
                  className={clsx('h-2.5 w-2.5 rounded-full', {
                    'bg-green-300':
                      row.original.g360_status === OrderStatus.SUBMITTED,
                    'bg-purple-300':
                      row.original.g360_status === OrderStatus.IN_PROGRESS,
                    'bg-red-500':
                      row.original.g360_status === OrderStatus.NEEDS_ATTENTION,
                    'bg-purple-600':
                      row.original.g360_status === OrderStatus.ON_HOLD,
                    'bg-blue-500':
                      row.original.g360_status === OrderStatus.PENDING_GRANITE,
                    'bg-yellow-300':
                      row.original.g360_status ===
                      OrderStatus.PENDING_SITE_SURVEY,
                    'bg-orange-300':
                      row.original.g360_status === OrderStatus.CONSTRUCTION,
                    'bg-accent-primary':
                      row.original.g360_status === OrderStatus.CONFIRMED,
                    'bg-green-600':
                      row.original.g360_status === OrderStatus.CONFIGURED,
                    'bg-teal-400':
                      row.original.g360_status === OrderStatus.COMPLETE,
                  })}
                />
                {row.original.g360_status}
              </p>
            );
          },
        },
        { id: 'order_site' },
        {
          header: () => null,
          id: 'order_number',
          accessorKey: 'order_number',
          cell: ({ row }) => <p>Order # {row.original.order_number}</p>,
        },
      ];
      return columns;
    };

  const getRowClassName = (row: Row<OrdersByLocationType>) => {
    if (row.original.overall_status === OrderStatus.NEEDS_ATTENTION)
      return '!bg-[#FF315E] !bg-opacity-[4%]';
    return '';
  };

  return (
    <ServerPaginatedTable
      data={tablePageData}
      columns={getOrdersIndexTableDef()}
      title="orders tracking table"
      sortingState={sortingState}
      onSortingChange={onSortingChange}
      showHeadersWhenEmpty={false}
      emptyDataElement={
        <EmptyOrdersIndex message="You haven’t placed any orders yet." />
      }
      tableContainerClassName="!grid-cols-[max-content_1fr_max-content_1fr_1fr]"
      styleVariant="grid"
      subRows={{
        name: 'orders_items',
        renderSubComponent: ({ row, isLastRow }) => (
          <SubRows
            row={row}
            isLastRow={isLastRow}
            subRowsName="orders_items"
            columns={getSubRowsOrdersIndexTableDef()}
          />
        ),
      }}
      getRowClassName={getRowClassName}
      {...paginatedTableProps}
    />
  );
};

export default LocationTable;
